import CommunityAccess from 'enums/community/access';
import { DOMHelpers } from 'helpers/dom';
import { Subscribable } from 'knockout';
import CommunityAccessManager from 'managers/community/access';
import PublicCommunityPagesManager from 'managers/community/pages/public';
import { instance as managers } from 'managers/context';
import CommunityPage from 'models/community/page';
import router from 'plugins/router';
import URLBuilder from '../builders/url';
import Shell from './shell';

const pageHelpers = require('helpers/page');

components.preload('master-viewer', 'community-navigation', 'task-activity');

const { discussionId, discussionId: communityId } = settings;

const specialPageTypes = [
    enums.CommunityPageType.NoAccess.value,
    enums.CommunityPageType.TermsAndConditionsChangeSummary.value,
    // the reason for removing this from special page types is it was not handled anywhere specifically. And it is now a loggedin members option to route to
    //enums.CommunityPageType.Unsubscribe.value 
]

export default class CommunityShell extends Shell {
    private visiblePage = ko.observable<CommunityPage>().extend({ notifyIfChanged: true });

    private pages: Subscribable<Array<CommunityPage>>
    private access: Subscribable<CommunityAccess>

    private isProcessingAccess: Subscribable<boolean>

    activePage = ko.observable<CommunityPage>().extend({ notifyIfChanged: true }); //used in pageView page

    visiblePageStyles: Subscribable<string>
    activeMasterContent: Subscribable<string>

    showVisibilePageStyles: Subscribable<boolean>

    constructor() {
        super();

        const accessManager = this.storage.get(CommunityAccessManager, { communityId });
        this.isProcessingAccess = accessManager.pluck(m => m.busy, true);
        this.access = accessManager.pluck(m => m.access, CommunityAccess.none);

        const pagesManager = this.storage.get(PublicCommunityPagesManager, { includeContent: true });
        const allPages = pagesManager.pluck(m => m.pages, []);
        const masterPages = allPages.filter(page => page.type() == enums.CommunityPageType.Master.value);
        this.pages = allPages.filter(page => page.type() != enums.CommunityPageType.Master.value);

        this.visiblePageStyles = this.visiblePage.pluck(p => p.styles, '');
        this.showVisibilePageStyles = this.visiblePage.pluck(p => p.isSystem, false);

        const activeMasterPage = ko.observable<CommunityPage>().extend({ notifyIfChanged: true });
        this.activeMasterContent = activeMasterPage.pluck(p => p.content, '');

        const activeInstruction = ko.pureComputed(() => router.activeInstruction());

        this.effects.register((instruction, masterPages) => {
            if (instruction != undefined) {
                const page = instruction.config.page;
                let masterPage = masterPages.find(p => p.id() == page.masterPageId());

                const mobileAppMaster = this.getMobileAppMaster(masterPages);
                if (mobileAppMaster != undefined) masterPage = mobileAppMaster;

                activeMasterPage(masterPage);
                this.activePage(page);
            }
        }, [activeInstruction, masterPages]);

        this.effects.register([
            DOMHelpers.assignClassToNode(document.body, 'community-layout')
        ]);
    }

    override getView() {
        return require('./community.html');
    }

    protected override checkIfUpdateAllowed() {
        return !this.isProcessingAccess();
    }

    protected getRoutes() {
        const routes = ko.pureComputed(() => {
            if (this.pages().length > 0) {
                switch (this.access()) {
                    case CommunityAccess.singlepage:
                        return this.getSinglePageRoutes(<number>settings.communityStandalonePageType);
                    case CommunityAccess.forbidden:
                        return this.getSinglePageRoutes(enums.CommunityPageType.NoAccess.value);
                    case CommunityAccess.twofactorauth:
                        return this.getSinglePageRoutes(enums.CommunityPageType.TwoFactorAuth.value);
                    case CommunityAccess.termsandconditions:
                        return this.getTermsRoutes();
                    case CommunityAccess.public:
                        return this.getRegularRoutes(false);
                    case CommunityAccess.membersonly:
                        return this.getRegularRoutes(true);
                }
            }

            return [];
        });

        return routes.filter(config => config.route != undefined);
    }

    protected override getInitialRoute() {
        return settings.initialRoute;
    }

    protected override afterActivate() {
        super.afterActivate();

        pageHelpers.adjustPageMessages();
        this.setupConfirmDialog();
        managers.discussionNotifications(discussionId).subscribe(_.noop);
    }

    protected override afterCompositionComplete() {
        super.afterCompositionComplete();

        this.visiblePage(this.activePage());

        _.defer(() => this.updateScrollr());
    }

    private setupConfirmDialog() {
        var $dialog = $('#wcc-confirm-dialog');

        $dialog.find('#wcc-confirm-dialog-heading').text(labels.Confirm);
        $dialog.find('#wcc-confirm-dialog-button-yes').text(labels.Yes);
        $dialog.find('#wcc-confirm-dialog-button-no').text(labels.Cancel);
    }

    private updateScrollr() {
        try {
            skrollr.get().refresh();
        } catch (ex) { }
    }

    private getRegularRoutes(isLoggedIn: boolean) {
        const pages = this.pages().filter(page => !_(specialPageTypes).contains(page.type()));

        const routes = pages
            .filter(page => page.access() != enums.CommunityPageAccess.LoggedInMembersOnly.value || isLoggedIn)
            .filter(page => page.access() != enums.CommunityPageAccess.NotLoggedIn.value || !isLoggedIn)
            .map(page => this.buildPageRoute(page));

        let defaultPage = pages.find(page => page.startUpOptions() == enums.CommunityPageStartUpOptions.IfLoggedIn.value && isLoggedIn ||
            page.startUpOptions() == enums.CommunityPageStartUpOptions.IfNotLoggedIn.value && !isLoggedIn);

        if (defaultPage == undefined)
            defaultPage = pages.find(page => page.startUpOptions() == enums.CommunityPageStartUpOptions.Always.value);

        var defaultRoutes = [defaultPage]
            .filter<CommunityPage>(page => page != undefined)
            .map(page => this.buildDefaultRoute(page));

        return routes.concat(defaultRoutes);
    }

    private getSinglePageRoutes(mainType: number, secondaryPageTypes: Array<number> = []) {
        const mainPages = this.pages().filter(page => page.type() == mainType);
        const secondaryPages = this.pages().filter(page => secondaryPageTypes.includes(<number>page.type()));

        const routes = mainPages.concat(secondaryPages).map(page => this.buildPageRoute(page));
        const defaultRoutes = mainPages.map(page => this.buildDefaultRoute(page));

        return routes.concat(defaultRoutes);
    }

    private getTermsRoutes() {
        const mainPages = this.pages().filter(page => page.type() == enums.CommunityPageType.TermsAndConditionsChangeSummary.value);

        const secondaryPages = this.pages()
            .filter(page => !specialPageTypes.includes(<number>page.type()))
            .filter(page => page.access() == enums.CommunityPageAccess.AlwaysVisible.value);

        const routes = mainPages.concat(secondaryPages).map(page => this.buildPageRoute(page));
        const defaultRoutes = mainPages.map(page => this.buildDefaultRoute(page));

        return routes.concat(defaultRoutes);
    }

    private buildPageRoute(page: CommunityPage) {
        return <DurandalRouteConfiguration>{
            route: page.url(),
            title: page.name(),
            moduleId: () => this.getPageModule(page),
            auth: page.access() == enums.CommunityPageAccess.LoggedInMembersOnly.value,
            page: page
        }
    }

    private buildDefaultRoute(page: CommunityPage) {
        return <DurandalRouteConfiguration>{
            route: '',
            title: page.name(),
            moduleId: () => this.getPageModule(page),
            auth: page.access() == enums.CommunityPageAccess.LoggedInMembersOnly.value,
            page: page,
            isDefault: true
        }
    }

    private getPageModule(page: CommunityPage) {
        switch (page.type()) {
            case enums.CommunityPageType.SignUp.value: return import('pages/community/signup/page');
            case enums.CommunityPageType.LogIn.value: return import('pages/community/login/page');
            case enums.CommunityPageType.Activity.value: return import('pages/community/activity/page');
            case enums.CommunityPageType.PasswordReset.value: return import('pages/community/password/reset/page');
            case enums.CommunityPageType.AccountActivation.value: return import('pages/community/password/reset/page');
            case enums.CommunityPageType.MemberProfile.value: return import('pages/community/profileforms/profileFormsPage');
            case enums.CommunityPageType.NoAccess.value: return import('pages/community/noaccess/page');
            case enums.CommunityPageType.TermsAndConditionsChangeSummary.value: return import('pages/community/termsChangeSummary/page');
            case enums.CommunityPageType.Unsubscribe.value: return import('pages/unsubscribe/page');
            case enums.CommunityPageType.TwoFactorAuth.value: return import('pages/account/twofactorauth/page');
            case enums.CommunityPageType.Referral.value: return import('pages/community/referral/page');
            case enums.CommunityPageType.CommunityMessage.value: return import('pages/community/discussion/message/communityDiscussionMessage');

            default: return import('pages/community/pageView/page');
        }
    }

    private getMobileAppMaster(masterPages: CommunityPage[]) {
        const url = URLBuilder.from(location.href);
        var masterName = url.getQuery('master');
        
        return masterPages.find(p => p.name()?.toLowerCase() == masterName?.toLowerCase());        
    }
}